import { endpoints } from 'api/endpoints'
import { GetAllData, GetAllResponse, GetArrayResponse, GetOneResponse } from 'interfaces/api.interfaces'
import {
  FeatureFlag,
  ForecastDriverCatalogItem,
  ForecastDriversCatalogResponse,
  Project,
  ProjectData,
  ProjectForecastDriver,
  ProjectForecastDriversResponse,
  ProjectForecastDriversUpdate,
  ProjectGetAllParams,
  ProjectLevel,
  ProjectType,
  ProjectUiVisibility,
} from 'interfaces/api/projects.interfaces'
import { axios } from 'modules/axios'
import { defaultPagination, withoutPagination } from 'utils/pagination'

const getAll = async (params?: ProjectGetAllParams): GetAllResponse<Project> => {
  const { data: response } = await axios.request<GetAllData<Project>>({
    method: 'GET',
    url: endpoints.projects.getAll(),
    params: {
      ...withoutPagination(params),
      ...defaultPagination(params),
    },
  })
  return response
}

const getSingle = async (id: number): GetOneResponse<Project> => {
  const { data: response } = await axios.request<Project>({
    method: 'GET',
    url: endpoints.projects.getSingle(id),
  })
  return response
}

const create = async (data: ProjectData): GetOneResponse<Project> => {
  const { data: response } = await axios.request<Project>({
    method: 'POST',
    url: endpoints.projects.create(),
    data,
  })
  return response
}

const update = async (id: number, data: Partial<ProjectData>): GetOneResponse<Project> => {
  const { data: response } = await axios.request<Project>({
    method: 'PATCH',
    url: endpoints.projects.update(id),
    data,
  })
  return response
}

const remove = async (id: number): Promise<void> =>
  axios.request({
    method: 'DELETE',
    url: endpoints.projects.remove(id),
    timeout: 0,
  })

const getTypes = async (): GetArrayResponse<ProjectType> => {
  const { data: response } = await axios.request<ProjectType[]>({
    method: 'GET',
    url: endpoints.projects.getTypes(),
  })
  return response
}

const getLevels = async (id: number): GetArrayResponse<ProjectLevel> => {
  const { data: response } = await axios.request<ProjectLevel[]>({
    method: 'GET',
    url: endpoints.projects.getLevels(id),
  })
  return response
}

const getFeatureFlags = async (): GetArrayResponse<FeatureFlag> => {
  const { data: response } = await axios.request<FeatureFlag[]>({
    method: 'GET',
    url: endpoints.projects.getFeatureFlags(),
  })
  return response
}

const unwrapDrivers = <T>(data: T[] | { drivers?: T[] } | undefined): T[] => {
  if (Array.isArray(data)) {
    return data
  }
  if (data?.drivers && Array.isArray(data.drivers)) {
    return data.drivers
  }
  return []
}

const getForecastDriversCatalog = async (): GetArrayResponse<ForecastDriverCatalogItem> => {
  const { data: response } = await axios.request<ForecastDriverCatalogItem[] | ForecastDriversCatalogResponse>({
    method: 'GET',
    url: endpoints.projects.getForecastDriversCatalog(),
  })
  return unwrapDrivers(response)
}

const getProjectForecastDrivers = async (projectId: number): GetArrayResponse<ProjectForecastDriver> => {
  const { data: response } = await axios.request<ProjectForecastDriver[] | ProjectForecastDriversResponse>({
    method: 'GET',
    url: endpoints.projects.getProjectForecastDrivers(projectId),
  })
  return unwrapDrivers(response)
}

const updateProjectForecastDrivers = async (
  projectId: number,
  data: ProjectForecastDriversUpdate,
): GetArrayResponse<ProjectForecastDriver> => {
  const { data: response } = await axios.request<ProjectForecastDriver[] | ProjectForecastDriversResponse>({
    method: 'PUT',
    url: endpoints.projects.updateProjectForecastDrivers(projectId),
    data,
  })
  return unwrapDrivers(response)
}

const getUiVisibility = async (projectId: number): GetOneResponse<ProjectUiVisibility> => {
  const { data: response } = await axios.request<ProjectUiVisibility>({
    method: 'GET',
    url: endpoints.projects.uiVisibility(projectId),
  })
  return response
}

const updateUiVisibility = async (projectId: number, visibleCodes: string[]): GetOneResponse<ProjectUiVisibility> => {
  const { data: response } = await axios.request<ProjectUiVisibility>({
    method: 'PUT',
    url: endpoints.projects.uiVisibility(projectId),
    data: { visibleCodes },
  })
  return response
}

export const projectsApi = {
  getUiVisibility,
  updateUiVisibility,
  getFeatureFlags,
  getForecastDriversCatalog,
  getProjectForecastDrivers,
  updateProjectForecastDrivers,
  getAll,
  getSingle,
  getLevels,
  create,
  update,
  remove,
  getTypes,
}
